import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import axios from "axios"
import InfoIcon from "@mui/icons-material/Info"

import {
  Container,
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  FormHelperText,
  CircularProgress,
  Badge,
} from "@mui/material"
import {
  Assignment,
  RateReview,
  Feedback,
  Timeline,
  Delete,
  Add,
  AccessTime,
  Flag,
  AccountTree,
  Visibility,
  Send,
  Clear,
  Email,
  Person,
  DateRange,
  PriorityHigh,
  Assessment,
  TrendingUp,
  Dashboard,
  FilterList,
  Search,
} from "@mui/icons-material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers"
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_URL}`,
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    //console.error("API Error:", error.response?.data || error.message)
    return Promise.reject(error)
  },
)

const teamGoals = {
  "Sales": [
    { id: "s1", title: "Increase Revenue", metric: "Revenue Growth", description: "Increase in overall sales revenue by a set percentage (e.g., 15% YoY)." },
    { id: "s2", title: "Improve Sales Performance", metric: "Sales Quota Attainment", description: "90%+ of sales reps achieving their sales quotas." },
    { id: "s3", title: "Maximize Lead Conversion", metric: "Lead Conversion Rate", description: "Achieve a conversion rate of 25% or higher." },
    { id: "s4", title: "Optimize Sales Cycle", metric: "Sales Cycle Length", description: "Decrease sales cycle time by 10%." },
    { id: "s5", title: "Improve Customer Acquisition Efficiency", metric: "Customer Acquisition Cost (CAC)", description: "Maintain CAC at or below $X per customer." },
    { id: "s6", title: "Increase Customer Lifetime Value", metric: "Customer Lifetime Value (CLV)", description: "Increase CLV by 20% through better customer relationships and upselling." },
    { id: "s7", title: "Increase Deal Size", metric: "Average Deal Size", description: "Increase average deal size by 15%." },
    { id: "s8", title: "Enhance Sales Team Productivity", metric: "Sales Activity", description: "Increase calls, meetings, or demos by 30% per sales rep." },
    { id: "s9", title: "Reduce Customer Churn", metric: "Churn Rate", description: "Reduce churn rate by 5% year-over-year." },
    { id: "s10", title: "Improve Forecasting Accuracy", metric: "Sales Forecast Accuracy", description: "Achieve 90%+ accuracy in monthly sales forecasts." },
  ],
  "Marketing": [
    { id: "m1", title: "Generate More Leads", metric: "Lead Generation", description: "Generate 25% more leads compared to last quarter." },
    { id: "m2", title: "Lower Lead Generation Costs", metric: "Cost Per Lead (CPL)", description: "Reduce CPL by 10% over the next quarter." },
    { id: "m3", title: "Improve Lead Quality", metric: "Marketing Qualified Leads (MQLs)", description: "Increase the number of MQLs by 15%." },
    { id: "m4", title: "Increase Conversion Rate", metric: "Conversion Rate", description: "Achieve a 20% conversion rate from lead to customer." },
    { id: "m5", title: "Maximize ROI on Marketing Efforts", metric: "Return on Investment (ROI)", description: "Achieve an ROI of 4:1 or better on marketing spend." },
    { id: "m6", title: "Increase Website Traffic", metric: "Website Traffic", description: "Increase website traffic by 30% over the next quarter." },
    { id: "m7", title: "Boost Email Engagement", metric: "Email Open Rate", description: "Achieve a 25% open rate for email campaigns." },
    { id: "m8", title: "Grow Social Media Presence", metric: "Social Media Engagement", description: "Increase social media interactions by 50% in the next 6 months." },
    { id: "m9", title: "Increase Brand Awareness", metric: "Brand Awareness", description: "Increase brand recognition score by 10% in target markets." },
    { id: "m10", title: "Improve Lead Nurturing", metric: "Lead Nurturing Success Rate", description: "Improve lead-to-customer conversion from nurtured leads by 20%." },
  ],
  "Finance": [
    { id: "f1", title: "Improve Profitability", metric: "Profit Margin", description: "Increase profit margin by 5% over the next fiscal year." },
    { id: "f2", title: "Optimize Operational Efficiency", metric: "Operating Expenses", description: "Reduce operational costs by 10% without sacrificing quality." },
    { id: "f3", title: "Maintain Cash Flow Stability", metric: "Cash Flow", description: "Ensure positive cash flow every quarter." },
    { id: "f4", title: "Improve Financial Leverage", metric: "Debt-to-Equity Ratio", description: "Maintain a debt-to-equity ratio of 0.5 or lower." },
    { id: "f5", title: "Enhance Forecast Accuracy", metric: "Financial Forecast Accuracy", description: "Achieve 95% or better accuracy in financial forecasting." },
    { id: "f6", title: "Increase Return on Assets", metric: "Return on Assets (ROA)", description: "Achieve a 12% return on assets annually." },
    { id: "f7", title: "Improve Working Capital Management", metric: "Working Capital", description: "Increase working capital by 10% by improving receivables and payables." },
    { id: "f8", title: "Increase Profit from Investments", metric: "Return on Investment (ROI)", description: "Achieve an ROI of 15% on capital expenditures." },
  ],
  "Product": [
    { id: "p1", title: "Increase Product Adoption", metric: "Feature Adoption Rate", description: "Achieve a 30% adoption rate for new features within the first 3 months." },
    { id: "p2", title: "Improve Product Quality", metric: "Product Quality", description: "Reduce product defect rate by 20%." },
    { id: "p3", title: "Speed up Time to Market", metric: "Time to Market", description: "Reduce time to market by 25% for the next product release." },
    { id: "p4", title: "Maintain Customer Satisfaction", metric: "Customer Satisfaction (CSAT)", description: "Achieve a customer satisfaction score of 90% or above." },
    { id: "p5", title: "Decrease Product Churn", metric: "Churn Rate", description: "Reduce churn rate by 10% year-over-year." },
    { id: "p6", title: "Improve Revenue per User", metric: "Revenue per User (ARPU)", description: "Increase ARPU by 20%." },
  ],
  "Supply Chain": [
    { id: "sc1", title: "Improve On-Time Deliveries", metric: "On-Time Delivery", description: "Achieve 95% or higher on-time delivery." },
    { id: "sc2", title: "Increase Inventory Efficiency", metric: "Inventory Turnover", description: "Increase inventory turnover rate by 15%." },
    { id: "sc3", title: "Reduce Supply Chain Costs", metric: "Supply Chain Costs", description: "Lower supply chain costs by 10%." },
    { id: "sc4", title: "Improve Demand Forecasting Accuracy", metric: "Demand Forecast Accuracy", description: "Achieve 90% forecast accuracy for demand." },
    { id: "sc5", title: "Improve Supplier Performance", metric: "Supplier Performance", description: "Achieve a 98% on-time and quality-compliant rate from suppliers." },
  ],
  "Engineering": [
    { id: "e1", title: "Increase Development Speed", metric: "Velocity", description: "Achieve 20% more story points completed per sprint." },
    { id: "e2", title: "Ensure High Code Quality", metric: "Code Quality", description: "Achieve a 90%+ code quality score with minimal bugs reported post-release." },
    { id: "e3", title: "Improve Deployment Frequency", metric: "Deployment Frequency", description: "Release new features or updates bi-weekly." },
    { id: "e4", title: "Enhance System Reliability", metric: "System Uptime", description: "Achieve 99.9% uptime in production systems." },
    { id: "e5", title: "Reduce Time to Recovery", metric: "Mean Time to Recovery (MTTR)", description: "Achieve a mean recovery time of under 30 minutes for critical issues." },
    { id: "e6", title: "Maintain High Test Coverage", metric: "Test Coverage", description: "Maintain 80% or higher automated test coverage." },
    { id: "e7", title: "Minimize Technical Debt", metric: "Technical Debt", description: "Reduce technical debt by 20% each quarter." },
  ],

};

const PerformanceManagement = () => {
  const [goals, setGoals] = useState([])
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' })
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })
  const [filters, setFilters] = useState({ department: "", status: "" })
  const [expandedGoalId, setExpandedGoalId] = useState(null)
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState({
    goals: false,
    employees: false,
  })

  // New states for pending goals
  const [pendingGoals, setPendingGoals] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()

  const [selectedTeam, setSelectedTeam] = useState("Custom")
  const [selectedGoalTemplate, setSelectedGoalTemplate] = useState(null)

  const [employeeTrainings, setEmployeeTrainings] = useState([])
  const [fetchingTrainings, setFetchingTrainings] = useState(false)

  const [newGoal, setNewGoal] = useState({
    title: "",
    description: "",
    employee_id: "",
    employee_email: "",
    dueDate: null,
    status: "Not Started",
    metrics: "",
    priority: "Medium",
    progress: 0,
    team: "Custom",
    training_id: "",
  })

  // State for showing employee-specific goals
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState("")

  // State for View Status tab
  const [statusViewEmployee, setStatusViewEmployee] = useState("")
  const [performanceData, setPerformanceData] = useState({
    goals: [],
    reviews: [],
    feedback: [],
    improvements: [],
    succession: [],
  })

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await Promise.all([fetchEmployees(), fetchGoals()])
      } catch (error) {
        //console.error("Error fetching initial data:", error)
      }
    }
    fetchInitialData()
  }, [])

  useEffect(() => {
    if (selectedGoalTemplate) {
      setNewGoal({
        ...newGoal,
        title: selectedGoalTemplate.title,
        description: selectedGoalTemplate.description,
        metrics: selectedGoalTemplate.metric,
        team: selectedTeam,
      })
    }
  }, [selectedGoalTemplate])

  // Auto-populate email and fetch assigned trainings when employee is selected
  useEffect(() => {
    const handleEmployeeChange = async () => {
      if (newGoal.employee_id) {
        const selectedEmployee = employees.find((emp) => emp.employee_id === newGoal.employee_id)
        if (selectedEmployee) {
          setNewGoal((prev) => ({
            ...prev,
            employee_email: selectedEmployee.email || "",
          }))
        }

        // Fetch trainings assigned to this employee
        try {
          setFetchingTrainings(true)
          const token = localStorage.getItem('token')
          const response = await api.get(`/api/trainings/employee/id/${newGoal.employee_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          setEmployeeTrainings(response.data || [])
        } catch (err) {
          //console.error("Error fetching employee trainings:", err)
          setEmployeeTrainings([])
        } finally {
          setFetchingTrainings(false)
        }
      } else {
        setNewGoal((prev) => ({
          ...prev,
          employee_email: "",
          training_id: "",
        }))
        setEmployeeTrainings([])
      }
    }

    handleEmployeeChange()
  }, [newGoal.employee_id, employees])

  // Fetch performance data when View Status tab is accessed
  useEffect(() => {
    if (location.pathname === "/status") {
      fetchPerformanceData()
    }
  }, [location.pathname, statusViewEmployee])

  const handleTabChange = (event, newValue) => {
    navigate(newValue)
  }

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity })
  }

  const fetchGoals = async () => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
    const employee_id = payload.employee_id;
      setLoading((prev) => ({ ...prev, goals: true }))
      const response = await api.get(`/api/goals?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      )
      setGoals(response.data || [])
      return response.data || []
    } catch (error) {
      //console.error("Error fetching goals:", error)
      openSnackbar(
        `Failed to load goals: ${error.response?.data?.message || error.message || "Unknown error"}`,
        "error",
      )
      return []
    } finally {
      setLoading((prev) => ({ ...prev, goals: false }))
    }
  }

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
    
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      let employee_id = payload.employee_id;

      if (!employee_id) {
        try {
          const userRes = await api.get(`/api/user/current`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (userRes.data && userRes.data.employee_id) {
            employee_id = userRes.data.employee_id;
          }
        } catch (e) {}
      }

      setLoading((prev) => ({ ...prev, employees: true }))

      const response = await api.get(`/api/goals/employees?employee_id=${employee_id || ''}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          
          }
        }
      )

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid employee data received from server")
      }

      const employeeData = response.data

      const normalizedEmployees = employeeData.map((emp) => ({
        employee_id: emp.employee_id || emp.id || emp._id,
        firstName: emp.firstName || emp.first_name || emp.fname || "",
        lastName: emp.lastName || emp.last_name || emp.lname||"",
        // department: emp.department || emp.dept || "General",
        email: emp.email || "",
        position: emp.position || emp.designation || emp.role || "",
      }))

      setEmployees(normalizedEmployees)
      return normalizedEmployees
    } catch (error) {
      //console.error("Error fetching employees:", error)
      openSnackbar(
        `Failed to load employees: ${error.response?.data?.message || error.message || "Unknown error"}`,
        "error",
      )
      setEmployees([])
      return []
    } finally {
      setLoading((prev) => ({ ...prev, employees: false }))
    }
  }

  const fetchPerformanceData = async () => {
    try {
        const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setLoading((prev) => ({ ...prev, performance: true }))

      // Fetch all performance-related data
      const promises = [
        api.get(`/api/goals`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        ),
      
        // Add other endpoints when available
        // api.get("/reviews"),
        // api.get("/feedback"),
        // api.get("/improvements"),
        // api.get("/succession"),
      ]

      const [goalsResponse] = await Promise.all(promises)

      setPerformanceData({
        goals: goalsResponse.data || [],
        reviews: [], // Will be populated when endpoint is available
        feedback: [], // Will be populated when endpoint is available
        improvements: [], // Will be populated when endpoint is available
        succession: [], // Will be populated when endpoint is available
      })
    } catch (error) {
      //console.error("Error fetching performance data:", error)
      openSnackbar("Failed to load performance data", "error")
    } finally {
      setLoading((prev) => ({ ...prev, performance: false }))
    }
  }

  const addGoalToPending = () => {
    if (!newGoal.title || !newGoal.dueDate || !newGoal.employee_id) {
      openSnackbar("Please fill in all required fields", "error")
      return
    }

    const selectedEmployee = employees.find((emp) => emp.employee_id === newGoal.employee_id)
    const selectedTraining = employeeTrainings.find((t) => t.id === newGoal.training_id)
    const goalToAdd = {
      ...newGoal,
      id: Date.now(),
      employeeName: selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : "Unknown",
      trainingTitle: selectedTraining ? selectedTraining.title : "",
      dueDate: newGoal.dueDate ? new Date(newGoal.dueDate).toISOString().split("T")[0] : null,
      isPending: true,
    }

    setPendingGoals((prev) => [...prev, goalToAdd])

    // Reset form
    setNewGoal({
      title: "",
      description: "",
      employee_id: "",
      employee_email: "",
      dueDate: null,
      status: "Not Started",
      metrics: "",
      priority: "Medium",
      progress: 0,
      team: "Custom",
      training_id: "",
    })

    setSelectedTeam("Custom")
    setSelectedGoalTemplate(null)

    openSnackbar("Goal added to pending list. Click Submit to create all goals and send email notifications.", "info")
  }

  const submitAllGoals = async () => {
    if (pendingGoals.length === 0) {
      openSnackbar("No pending goals to submit", "warning");
      return;
    }
  
    try {
      setSubmitting(true);
  
      // Extract company_id from JWT
      const token = localStorage.getItem('token');
      if (!token) throw new Error("No authentication token found.");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
  
      // Prepare goals (existing logic)
      const goalsToSubmit = pendingGoals.map((goal) => ({
        ...goal,
        isPending: undefined,
        id: undefined,
      }));
  
      // Send goals via POST with company_id in query
      const response = await api.post(
        `/api/goals/create-multiple`, 
        { goals: goalsToSubmit },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      setGoals((prevGoals) => [...response.data, ...prevGoals]);
      setPendingGoals([]);
  
      openSnackbar(`Successfully created ${response.data.length} goals and sent email notifications!`, "success");
  
    } catch (error) {
      //console.error("Error submitting goals:", error);
      const errorMessage = error.response?.data?.message || error.message || "An unknown error occurred";
      openSnackbar(`Failed to submit goals: ${errorMessage}`, "error");
    } finally {
      setSubmitting(false);
    }
  };
  

  const removePendingGoal = (goalId) => {
    setPendingGoals((prev) => prev.filter((goal) => goal.id !== goalId))
    openSnackbar("Goal removed from pending list", "info")
  }

  const clearAllPendingGoals = () => {
    setPendingGoals([])
    openSnackbar("All pending goals cleared", "info")
  }

  const toggleExpandGoal = (id) => {
    setExpandedGoalId(expandedGoalId === id ? null : id)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success"
      case "In Progress":
        return "warning"
      case "Not Started":
        return "default"
      case "Overdue":
        return "error"
      case "Active":
        return "info"
      case "Terminated":
        return "error"
      default:
        return "default"
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "High":
        return "error"
      case "Medium":
        return "warning"
      case "Low":
        return "info"
      default:
        return "default"
    }
  }

  // Filter goals based on selected employee for viewing - only show when employee is selected
  const filteredGoalsForDisplay = useMemo(() => {
    if (!goals || goals.length === 0) return []

    // Only show goals if an employee is specifically selected
    if (!selectedEmployeeForView) return []

    const filtered = goals.filter((goal) => goal.employee_id === selectedEmployeeForView)

    // Apply other filters
    return filtered.filter((goal) => {
      const employee = employees.find((emp) => emp.employee_id === goal.employee_id)
      return (
        (!filters.department || (employee && employee.department === filters.department)) &&
        (!filters.status || goal.status === filters.status)
      )
    })
  }, [goals, employees, filters, selectedEmployeeForView])

  const getEmployeeName = (employeeId) => {
    if (!employeeId) return "Unassigned"
    const emp = employees.find((e) => e.employee_id === employeeId)
    return emp ? `${emp.firstName} ${emp.lastName}` : employeeId
  }

  const handleTeamChange = (event) => {
    const team = event.target.value
    setSelectedTeam(team)
    setSelectedGoalTemplate(null)
    setNewGoal({
      ...newGoal,
      team: team,
    })
  }

  const handleGoalTemplateChange = (event) => {
    const templateId = event.target.value
    if (!templateId) {
      setSelectedGoalTemplate(null)
      return
    }

    const template = teamGoals[selectedTeam].find((goal) => goal.id === templateId)
    if (template) {
      setSelectedGoalTemplate(template)
    }
  }

  // Comprehensive View Status Component
  const ViewStatusComponent = () => {
    const filteredPerformanceData = useMemo(() => {
      if (!statusViewEmployee) return performanceData

      return {
        goals: performanceData.goals.filter((goal) => goal.employee_id === statusViewEmployee),
        reviews: performanceData.reviews.filter((review) => review.employee_id === statusViewEmployee),
        feedback: performanceData.feedback.filter((feedback) => feedback.employee_id === statusViewEmployee),
        improvements: performanceData.improvements.filter(
          (improvement) => improvement.employee_id === statusViewEmployee,
        ),
        succession: performanceData.succession.filter((succession) => succession.employee_id === statusViewEmployee),
      }
    }, [performanceData, statusViewEmployee])


   

  

  }
  

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ maxWidth: 1500, p: 3,  borderRadius: 2, // Rounded corners
  border: '2px solid',
  borderColor: 'grey.300', // Subtle professional border
  boxShadow: 4,  mx: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom  fontWeight="bold">
            Performance Management 
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={location.pathname}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiTab-root": { fontWeight: "medium", py: 2 },
              }}
            >
              <Tab icon={<Assignment />} label="Goals & Objectives" value="/performance" />
              <Tab icon={<RateReview />} label="Performance Reviews" value="/review" />
              <Tab icon={<Feedback />} label="Feedback System" value="/feedback" />
              <Tab icon={<Timeline />} label="Improvement Plans" value="/improve" />
              <Tab icon={<AccountTree />} label="Succession Planning" value="/succession" />
              <Tab icon={<Visibility />} label="View Status" value="/performancestatus" />
            </Tabs>
          </Box>
        </Paper>

        {/* View Status Tab */}
        {location.pathname === "/status" && <ViewStatusComponent />}

        {location.pathname === "/performance" && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Left side - Create Goal */}
              <Grid item xs={12} md={4}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", color: "#1976d2" }}
                    >
                      <Flag sx={{ mr: 1 }} /> Set SMART Goals
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Tooltip title="Select the team/department for predefined goal templates" arrow placement="right">
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Team</InputLabel>
                        <Select value={selectedTeam} onChange={handleTeamChange} label="Team">
                          <MenuItem value="Custom">Custom Goal</MenuItem>
                          <MenuItem value="Sales">Sales</MenuItem>
                          <MenuItem value="Marketing">Marketing</MenuItem>
                          <MenuItem value="Finance">Finance</MenuItem>
                          <MenuItem value="Product">Product</MenuItem>
                          <MenuItem value="Supply Chain">Supply Chain</MenuItem>
                          <MenuItem value="Engineering">Engineering</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip title="Select the employee who will be assigned this goal" arrow placement="right">
                      <FormControl fullWidth margin="normal">
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Person sx={{ mr: 0.5, fontSize: 16 }} />
                            Employee Name *
                          </Box>
                        </InputLabel>
                        <Select
                          value={newGoal.employee_id}
                          onChange={(e) => setNewGoal({ ...newGoal, employee_id: e.target.value })}
                          label="Employee Name"
                          required
                          disabled={loading.employees}
                        >
                          <MenuItem value="">
                            <em>{loading.employees ? "Loading employees..." : "Select Employee"}</em>
                          </MenuItem>
                          {employees.length > 0
                            ? employees.map((employee) => (
                                <MenuItem key={employee.employee_id} value={employee.employee_id}>
                                  {`${employee.firstName} ${employee.lastName}`}
                                  {employee.department && ` - ${employee.department}`}
                                </MenuItem>
                              ))
                            : !loading.employees && (
                                <MenuItem disabled>
                                  <em>No employees found</em>
                                </MenuItem>
                              )}
                        </Select>
                        {loading.employees && <LinearProgress sx={{ mt: 1 }} />}
                      </FormControl>
                    </Tooltip>

                    {/* Employee Email Field */}
                    <Tooltip title="Company email address (automatically fetched from database)" arrow placement="right">
                      <TextField
                        fullWidth
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Email sx={{ mr: 0.5, fontSize: 16 }} />
                            Email
                          </Box>
                        }
                        value={newGoal.employee_email}
                        margin="normal"
                        disabled
                          helperText="Email is automatically fetching based on employee name"
                        InputProps={{
                          style: { backgroundColor: "#f5f5f5" },
                        }}
                      />
                    </Tooltip>

                    <Tooltip title="Link this goal to an assigned training course (Optional)" arrow placement="right">
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Prerequisite Training (Optional)</InputLabel>
                        <Select
                          value={newGoal.training_id || ""}
                          onChange={(e) => setNewGoal({ ...newGoal, training_id: e.target.value })}
                          label="Prerequisite Training (Optional)"
                          disabled={fetchingTrainings}
                        >
                          <MenuItem value="">
                            <em>{fetchingTrainings ? "Loading trainings..." : "None - No training needed"}</em>
                          </MenuItem>
                          {employeeTrainings.map((training) => (
                            <MenuItem key={training.id} value={training.id}>
                              {training.title}
                            </MenuItem>
                          ))}
                        </Select>
                        {fetchingTrainings && <LinearProgress sx={{ mt: 1 }} />}
                      </FormControl>
                    </Tooltip>

                    {selectedTeam !== "Custom" && (
                      <Tooltip title="Choose from predefined goal templates for the selected team" arrow placement="right">
                        <FormControl fullWidth margin="normal">
                          <InputLabel>Goal Template</InputLabel>
                          <Select
                            value={selectedGoalTemplate ? selectedGoalTemplate.id : ""}
                            onChange={handleGoalTemplateChange}
                            label="Goal Template"
                          >
                            <MenuItem value="">
                              <em>Select a predefined goal</em>
                            </MenuItem>
                            {teamGoals[selectedTeam].map((goal) => (
                              <MenuItem key={goal.id} value={goal.id}>
                                {goal.title}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Tooltip>
                    )}

                    <Tooltip title="Enter a clear and specific goal title" arrow placement="right"> 
                      <TextField
                        fullWidth
                        label="Goal Title "
                        value={newGoal.title}
                        onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                        margin="normal"
                        required
                        placeholder="Enter a specific, measurable goal title..."
                      />
                    </Tooltip>

                    <Tooltip title="Provide detailed description of what needs to be achieved" arrow placement="right">
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={newGoal.description}
                        onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                        margin="normal"
                        placeholder="Provide a detailed description of the goal, including specific targets and expectations..."
                      />
                    </Tooltip>

                    <Tooltip title="Select the key metric that will be used to measure success" arrow placement="right"> 
                      <FormControl fullWidth margin="normal">
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Assessment sx={{ mr: 0.5, fontSize: 16 }} />
                            Metric to Evaluate
                          </Box>
                        </InputLabel>
                        <Select
                          value={newGoal.metrics}
                          onChange={(e) => setNewGoal({ ...newGoal, metrics: e.target.value })}
                          label="Metric to Evaluate"
                        >
                         <MenuItem value=""><em>Select Metric</em></MenuItem>
                    
                    <MenuItem value="Revenue Growth">Revenue Growth</MenuItem>
                    <MenuItem value="Sales Quota Attainment">Sales Quota Attainment</MenuItem>
                    <MenuItem value="Lead Conversion Rate">Lead Conversion Rate</MenuItem>
                    <MenuItem value="Sales Cycle Length">Sales Cycle Length</MenuItem>
                    <MenuItem value="Customer Acquisition Cost (CAC)">Customer Acquisition Cost (CAC)</MenuItem>
                    <MenuItem value="Customer Lifetime Value (CLV)">Customer Lifetime Value (CLV)</MenuItem>
                    <MenuItem value="Average Deal Size">Average Deal Size</MenuItem>
                    <MenuItem value="Sales Activity">Sales Activity</MenuItem>
                    <MenuItem value="Churn Rate">Churn Rate</MenuItem>
                    <MenuItem value="Sales Forecast Accuracy">Sales Forecast Accuracy</MenuItem>

          
                    <MenuItem value="Lead Generation">Lead Generation</MenuItem>
                    <MenuItem value="Cost Per Lead (CPL)">Cost Per Lead (CPL)</MenuItem>
                    <MenuItem value="Marketing Qualified Leads (MQLs)">Marketing Qualified Leads (MQLs)</MenuItem>
                    <MenuItem value="Conversion Rate">Conversion Rate</MenuItem>
                    <MenuItem value="Return on Investment (ROI)">Return on Investment (ROI)</MenuItem>
                    <MenuItem value="Website Traffic">Website Traffic</MenuItem>
                    <MenuItem value="Email Open Rate">Email Open Rate</MenuItem>
                    <MenuItem value="Social Media Engagement">Social Media Engagement</MenuItem>
                    <MenuItem value="Brand Awareness">Brand Awareness</MenuItem>
                    <MenuItem value="Lead Nurturing Success Rate">Lead Nurturing Success Rate</MenuItem>

            
                    <MenuItem value="Profit Margin">Profit Margin</MenuItem>
                    <MenuItem value="Operating Expenses">Operating Expenses</MenuItem>
                    <MenuItem value="Cash Flow">Cash Flow</MenuItem>
                    <MenuItem value="Debt-to-Equity Ratio">Debt-to-Equity Ratio</MenuItem>
                    <MenuItem value="Financial Forecast Accuracy">Financial Forecast Accuracy</MenuItem>
                    <MenuItem value="Return on Assets (ROA)">Return on Assets (ROA)</MenuItem>
                    <MenuItem value="Working Capital">Working Capital</MenuItem>

                    <MenuItem value="Feature Adoption Rate">Feature Adoption Rate</MenuItem>
                    <MenuItem value="Product Quality">Product Quality</MenuItem>
                    <MenuItem value="Time to Market">Time to Market</MenuItem>
                    <MenuItem value="Customer Satisfaction (CSAT)">Customer Satisfaction (CSAT)</MenuItem>
                    <MenuItem value="Revenue per User (ARPU)">Revenue per User (ARPU)</MenuItem>

                   
                    <MenuItem value="On-Time Delivery">On-Time Delivery</MenuItem>
                    <MenuItem value="Inventory Turnover">Inventory Turnover</MenuItem>
                    <MenuItem value="Supply Chain Costs">Supply Chain Costs</MenuItem>
                    <MenuItem value="Demand Forecast Accuracy">Demand Forecast Accuracy</MenuItem>
                    <MenuItem value="Supplier Performance">Supplier Performance</MenuItem>

                
                    <MenuItem value="Velocity">Velocity</MenuItem>
                    <MenuItem value="Code Quality">Code Quality</MenuItem>
                    <MenuItem value="Deployment Frequency">Deployment Frequency</MenuItem>
                    <MenuItem value="System Uptime">System Uptime</MenuItem>
                    <MenuItem value="Mean Time to Recovery (MTTR)">Mean Time to Recovery (MTTR)</MenuItem>
                    <MenuItem value="Test Coverage">Test Coverage</MenuItem>
                    <MenuItem value="Technical Debt">Technical Debt</MenuItem>


                  </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip title="Set the priority level to help with resource allocation and focus" arrow placement="right">
                      <FormControl fullWidth margin="normal">
                        <InputLabel>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                          
                            Priority
                          </Box>
                        </InputLabel>
                        <Select
                          value={newGoal.priority}
                          onChange={(e) => setNewGoal({ ...newGoal, priority: e.target.value })}
                          label="Priority"
                        >
                          <MenuItem value="High">High - Critical for business success</MenuItem>
                          <MenuItem value="Medium">Medium - Important but not urgent</MenuItem>
                          <MenuItem value="Low">Low - Nice to have</MenuItem>
                        </Select>
                      </FormControl>
                    </Tooltip>

                    <Tooltip title="Set the target completion date for this goal" arrow>
                      <DatePicker
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <DateRange sx={{ mr: 0.5, fontSize: 16 }} />
                            Due Date *
                          </Box>
                        }
                        value={newGoal.dueDate}
                        onChange={(date) => setNewGoal({ ...newGoal, dueDate: date })}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="normal"
                            required
                            helperText="Select a realistic target date for goal completion"
                          />
                        )}
                        sx={{ marginTop: "17px" }}
                      />
                    </Tooltip>

                    <Box sx={{ mt: 3, textAlign: "center" }}>
                      <Tooltip title="Add this goal to the pending list for review before submission" arrow>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={addGoalToPending}
                          disabled={loading.goals}
                          sx={{
                            bgcolor: "#1976d2",
                            color: "white",
                            px: 4,
                            py: 1.5,
                            fontSize: "1rem",
                            fontWeight: "medium",
                          }}
                        >
                          Add Goal
                        </Button>
                      </Tooltip>
                      <FormHelperText sx={{ mt: 1, textAlign: "center", fontSize: "0.875rem" }}>
                        After creating goals, click "Submit All Goals" to finalize and send email notifications
                      </FormHelperText>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right side - Pending Goals and Employee Goals */}
              <Grid item xs={12} md={8}>
                {/* Pending Goals Section */}
                {pendingGoals.length > 0 && (
                  <Paper elevation={3} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bgcolor: "#fff3e0",
                      }}
                    >
                      <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                        <AccessTime sx={{ mr: 1 }} /> Pending Goals ({pendingGoals.length})
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Remove all pending goals" arrow>
                          <Button variant="outlined" startIcon={<Clear />} onClick={clearAllPendingGoals} size="small">
                            Clear All
                          </Button>
                        </Tooltip>
                        <Tooltip title="Submit all pending goals and send email notifications to employees" arrow>
                          <Button
                            variant="contained"
                            startIcon={<Send />}
                            onClick={submitAllGoals}
                            disabled={submitting}
                            sx={{ bgcolor: "#4caf50" }}
                          >
                            {submitting ? "Submitting..." : "Submit All Goals"}
                          </Button>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Divider />
                    <List>
                      {pendingGoals.map((goal) => (
                        <ListItem key={goal.id}>
                          <ListItemText
                            primary={goal.title}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  Assigned to: {goal.employeeName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Email: {goal.employee_email}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Due: {goal.dueDate ? dayjs(goal.dueDate).tz(companyConfig.timezone).format(companyConfig.dateFormat) : "No due date"}
                                </Typography>
                                {goal.trainingTitle && (
                                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                                    Prerequisite Training: {goal.trainingTitle}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip label={goal.priority} color={getPriorityColor(goal.priority)} size="small" />
                              <Tooltip title="Remove this goal from pending list" arrow>
                                <IconButton
                                  edge="end"
                                  onClick={() => removePendingGoal(goal.id)}
                                  color="error"
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {/* Employee Goals Section - Only shows when employee is selected */}
                <Paper elevation={3}>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                      <Search sx={{ mr: 1 }} /> Employee Goals
                      {selectedEmployeeForView && ` (${filteredGoalsForDisplay.length})`}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Tooltip title="Select an employee to view their goals" arrow placement="left">
                        <FormControl size="small" sx={{ minWidth: 280 }}>
                          <InputLabel >Select Employee to View Goals</InputLabel>
                          <Select
                            value={selectedEmployeeForView}
                            onChange={(e) => setSelectedEmployeeForView(e.target.value)}
                            label="Select Employee to View Goals"
                            
                          >
                            <MenuItem value="">
                              <em>Choose an employee</em>
                            </MenuItem>
                            {employees.map((employee) => (
                              <MenuItem key={employee.employee_id} value={employee.employee_id}>
                                {`${employee.firstName} ${employee.lastName}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Tooltip>
                      {selectedEmployeeForView && (
                        <Tooltip title="Filter goals by status" arrow>
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Filter by Status</InputLabel>
                            <Select
                              value={filters.status}
                              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                              label="Filter by Status"
                            >
                              <MenuItem value="">All Statuses</MenuItem>
                              <MenuItem value="Not Started">Not Started</MenuItem>
                              <MenuItem value="In Progress">In Progress</MenuItem>
                              <MenuItem value="Completed">Completed</MenuItem>
                              <MenuItem value="Overdue">Overdue</MenuItem>
                            </Select>
                          </FormControl>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  <Divider />

                  {loading.goals ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <CircularProgress />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Loading goals...
                      </Typography>
                    </Box>
                  ) : !selectedEmployeeForView ? (
                    <Box sx={{ p: 4, textAlign: "center" }}>
                      <Search sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Select an Employee
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Choose an employee from the dropdown above to view their assigned goals.
                      </Typography>
                    </Box>
                  ) : filteredGoalsForDisplay.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                      <Typography variant="body1">
                        No goals found for {getEmployeeName(selectedEmployeeForView)}.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {filteredGoalsForDisplay.map((goal) => (
                        <React.Fragment key={goal.id || goal._id}>
                          <ListItem
                            button
                            onClick={() => toggleExpandGoal(goal.id || goal._id)}
                            sx={{
                              borderLeft: "4px solid",
                              borderLeftColor:
                                getPriorityColor(goal.priority) === "error"
                                  ? "#f44336"
                                  : getPriorityColor(goal.priority) === "warning"
                                    ? "#ff9800"
                                    : "#2196f3",
                              mb: 1,
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <Typography variant="subtitle1" fontWeight="medium">
                                    {goal.title}
                                  </Typography>
                                  {goal.completionDetails && (
                                    <Tooltip title="Employee has provided completion details">
                                      <InfoIcon fontSize="small" color="primary" sx={{ ml: 1 }} />
                                    </Tooltip>
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Due: {goal.dueDate ? dayjs(goal.dueDate).tz(companyConfig.timezone).format(companyConfig.dateFormat) : "No due date"}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Progress: {goal.progress || 0}%
                                  </Typography>
                                  {goal.progressUpdates && goal.progressUpdates.length > 0 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Last update:{" "}
                                      {dayjs(goal.progressUpdates[goal.progressUpdates.length - 1].date).tz(companyConfig.timezone).format(companyConfig.dateFormat)}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Chip
                                  label={goal.status}
                                  color={getStatusColor(goal.status)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                <Chip label={goal.priority} color={getPriorityColor(goal.priority)} size="small" />
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>

                          {expandedGoalId === (goal.id || goal._id) && (
                            <Box sx={{ px: 4, pb: 2 }}>
                              <Paper elevation={2} sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="body1" paragraph>
                                      <strong>Description:</strong> {goal.description}
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                      <strong>Metrics:</strong> {goal.metrics}
                                    </Typography>
                                    {goal.training && (
                                      <Typography variant="body1" color="success.main" sx={{ fontWeight: 'medium' }}>
                                        <strong>Prerequisite Training:</strong> {goal.training.title}
                                      </Typography>
                                    )}
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <Typography variant="body1">
                                      <strong>Team:</strong> {goal.team}
                                    </Typography>
                                    <Typography variant="body1">
                                      <strong>Priority:</strong> {goal.priority}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                {goal.progressUpdates?.length > 0 && (
                                  <Box sx={{ mt: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                      Progress History
                                    </Typography>
                                    {goal.progressUpdates.map((update, index) => (
                                      <Box key={index} sx={{ mb: 2, p: 2, bgcolor: "white", borderRadius: 1 }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                          <Typography variant="caption" color="textSecondary">
                                            {dayjs(update.date).tz(companyConfig.timezone).format(`${companyConfig.dateFormat} hh:mm A`)}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2">{update.note}</Typography>
                                        <Box sx={{ mt: 1 }}>
                                          <Chip
                                            label={update.status}
                                            size="small"
                                            color={getStatusColor(update.status)}
                                            sx={{ mr: 1 }}
                                          />
                                          <Chip
                                            label={`${update.progress}%`}
                                            size="small"
                                            color={
                                              update.progress >= 75
                                                ? "success"
                                                : update.progress >= 50
                                                  ? "warning"
                                                  : "error"
                                            }
                                          />
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Paper>
                            </Box>
                          )}
                          <Divider />
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  )
}

export default PerformanceManagement